import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Users,
  DollarSign,
  TrendingUp,
  Repeat,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import type { CustomerListResponse } from '../../api/customers';
import { fetchCustomers } from '../../api/customers';
import CustomerDetailModal from './components/CustomerDetailModal';
import { formatCurrency, formatDate } from './components/CustomerDetailModal';

type SortField = 'name' | 'email' | 'ticket_count' | 'total_spent' | 'last_visit' | 'workshop_count';

const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'last_visit', label: 'Last Visit' },
  { value: 'total_spent', label: 'Total Spent' },
  { value: 'ticket_count', label: 'Tickets' },
  { value: 'workshop_count', label: 'Workshops' },
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
];

const PAGE_SIZE = 50;

const CustomerManagement = () => {
  const [data, setData] = useState<CustomerListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortField>('last_visit');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [offset, setOffset] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchCustomers({ search, sort, sortDir, limit: PAGE_SIZE, offset })
      .then(setData)
      .catch((err) => console.error('[Customers] Failed:', err))
      .finally(() => setLoading(false));
  }, [search, sort, sortDir, offset]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      setSearch(searchInput);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const toggleSort = (field: SortField) => {
    if (sort === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setSortDir(field === 'name' || field === 'email' ? 'asc' : 'desc');
    }
    setOffset(0);
  };

  const summary = data?.summary;
  const pagination = data?.pagination;
  const customers = data?.customers || [];
  const total = pagination?.total || 0;
  const pageStart = offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white dark:bg-[#1a1f1c] border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-bark-900 dark:text-[#e8ece9]">Customers</h1>
              <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">
                {loading ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 size={12} className="animate-spin" /> Loading customers...
                  </span>
                ) : (
                  'Customer lookup and order history'
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-600 dark:text-stone-400">Total Customers</p>
                <p className="text-2xl font-bold text-bark-900 dark:text-[#e8ece9] mt-1">{summary?.totalCustomers?.toLocaleString() ?? '--'}</p>
              </div>
              <Users className="text-moss-600" size={24} />
            </div>
          </div>
          <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-600 dark:text-stone-400">Total Revenue</p>
                <p className="text-2xl font-bold text-bark-900 dark:text-[#e8ece9] mt-1">{summary ? formatCurrency(summary.totalRevenue) : '--'}</p>
              </div>
              <DollarSign className="text-moss-600" size={24} />
            </div>
          </div>
          <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-600 dark:text-stone-400">Avg Spend</p>
                <p className="text-2xl font-bold text-bark-900 dark:text-[#e8ece9] mt-1">{summary ? formatCurrency(summary.avgSpend) : '--'}</p>
              </div>
              <TrendingUp className="text-water-600" size={24} />
            </div>
          </div>
          <div className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-600 dark:text-stone-400">Repeat Customers</p>
                <p className="text-2xl font-bold text-bark-900 dark:text-[#e8ece9] mt-1">{summary?.repeatCustomerCount?.toLocaleString() ?? '--'}</p>
              </div>
              <Repeat className="text-purple-600" size={24} />
            </div>
          </div>
        </div>

        {/* Search + sort bar */}
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border p-4 mb-4 flex items-center gap-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 dark:bg-[#1e2520] dark:text-[#e8ece9]"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-stone-600 dark:text-stone-400">
            <span>Sort by</span>
            <select
              value={sort}
              onChange={(e) => {
                const val = e.target.value as SortField;
                setSort(val);
                setSortDir(val === 'name' || val === 'email' ? 'asc' : 'desc');
                setOffset(0);
              }}
              className="border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 dark:bg-[#1e2520] dark:text-[#e8ece9]"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              className="px-2 py-1.5 border rounded-lg hover:bg-stone-50 dark:hover:bg-[#242d28] text-xs"
            >
              {sortDir === 'asc' ? 'A-Z' : 'Z-A'}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 dark:bg-[#1e2520] text-stone-600 dark:text-stone-400 text-left">
              <tr>
                <th className="px-4 py-3 font-medium cursor-pointer hover:text-bark-900 dark:hover:text-[#e8ece9]" onClick={() => toggleSort('name')}>
                  Name {sort === 'name' && (sortDir === 'asc' ? '\u2191' : '\u2193')}
                </th>
                <th className="px-4 py-3 font-medium cursor-pointer hover:text-bark-900 dark:hover:text-[#e8ece9]" onClick={() => toggleSort('email')}>
                  Email {sort === 'email' && (sortDir === 'asc' ? '\u2191' : '\u2193')}
                </th>
                <th className="px-4 py-3 font-medium text-right cursor-pointer hover:text-bark-900 dark:hover:text-[#e8ece9]" onClick={() => toggleSort('workshop_count')}>
                  Workshops {sort === 'workshop_count' && (sortDir === 'asc' ? '\u2191' : '\u2193')}
                </th>
                <th className="px-4 py-3 font-medium text-right cursor-pointer hover:text-bark-900 dark:hover:text-[#e8ece9]" onClick={() => toggleSort('total_spent')}>
                  Total Spent {sort === 'total_spent' && (sortDir === 'asc' ? '\u2191' : '\u2193')}
                </th>
                <th className="px-4 py-3 font-medium cursor-pointer hover:text-bark-900 dark:hover:text-[#e8ece9]" onClick={() => toggleSort('last_visit')}>
                  Last Visit {sort === 'last_visit' && (sortDir === 'asc' ? '\u2191' : '\u2193')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && customers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-stone-500 dark:text-stone-400">
                    <Loader2 size={20} className="animate-spin inline mr-2" /> Loading customers...
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-stone-500 dark:text-stone-400">
                    {search ? 'No customers match your search' : 'No customer data yet'}
                  </td>
                </tr>
              ) : (
                customers.map((c) => (
                  <tr
                    key={c.email}
                    onClick={() => setSelectedEmail(c.email)}
                    className="hover:bg-moss-50/50 dark:hover:bg-[#242d28] cursor-pointer transition"
                  >
                    <td className="px-4 py-3 font-medium text-bark-900 dark:text-[#e8ece9]">
                      {c.firstName} {c.lastName}
                    </td>
                    <td className="px-4 py-3 text-stone-600 dark:text-stone-400">{c.email}</td>
                    <td className="px-4 py-3 text-right">{c.workshopCount}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(c.totalSpent)}</td>
                    <td className="px-4 py-3 text-stone-600 dark:text-stone-400">{formatDate(c.lastVisit)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="border-t px-4 py-3 flex items-center justify-between text-sm text-stone-600 dark:text-stone-400">
              <span>{pageStart}-{pageEnd} of {total.toLocaleString()}</span>
              <div className="flex items-center gap-2">
                <button
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  className="px-3 py-1.5 border rounded-lg hover:bg-stone-50 dark:hover:bg-[#242d28] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={14} className="inline -mt-0.5" /> Previous
                </button>
                <button
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  className="px-3 py-1.5 border rounded-lg hover:bg-stone-50 dark:hover:bg-[#242d28] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight size={14} className="inline -mt-0.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {selectedEmail && (
        <CustomerDetailModal
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
        />
      )}
    </div>
  );
};

export default CustomerManagement;
